/*
 * KARAAT diamond modules
 * Turns shortcode paragraphs in article bodies into interactive diamond illustrations.
 *
 * In the article body (Shopify rich text), write a paragraph that contains only a shortcode:
 *   [[karaat-qualities]]    three quality cards (Excellent, High, Good)
 *   [[karaat-4c-glance]]    4Cs chart: color, clarity and cut scales + quality toggle
 *   [[karaat-color]]        color scale D–Z with KARAAT range
 *   [[karaat-clarity]]      clarity scale FL–I3 with KARAAT range
 *   [[karaat-cut]]          light-path diagram: too shallow / Excellent / too deep
 *   [[karaat-carat]]        carat size slider, 0.25–3.00 ct
 *
 * Language comes from <html lang="..."> (en, fi, sv). Anything else falls back to English.
 * No dependencies. Renders inline SVG. Safe to load with `defer`.
 */
(function () {
  'use strict';

  var NS = 'http://www.w3.org/2000/svg';

  /* ------------------------------------------------------------------ strings */
  var STRINGS = {
    en: {
      dec: '.', karaatQuality: 'KARAAT quality', karaatRange: 'KARAAT range',
      q: { excellent: 'Excellent', high: 'High', good: 'Good' },
      popular: 'Most chosen', color: 'Color', clarity: 'Clarity', cut: 'Cut',
      read: {
        excellent: 'D–F color · VVS clarity · Excellent cut',
        high: 'D–F color · VS clarity · Excellent cut · most chosen',
        good: 'G–H color · VS clarity · Excellent cut'
      },
      cards: {
        excellent: ['Truly colorless and virtually inclusion-free, even at 10× magnification.', 'Best for: not compromising. The whitest, clearest diamond we offer.'],
        high: ['Truly colorless, with inclusions only a loupe will find.', 'Best for: colorless to the eye and clean to the loupe, with nothing given up you would ever notice.'],
        good: ['Near-colorless, with inclusions only a loupe will find.', 'Best for: going as large as you can. If carat is what you are after, this is where every millimeter counts.']
      },
      rows: {
        color: 'How white it is. D is icy white; down the alphabet it turns yellow.',
        clarity: 'How clean it is inside, as seen at 10× magnification.',
        cut: 'How well it is shaped to send light back to your eye.'
      },
      colorCats: ['Colorless', 'Near colorless', 'Faint', 'Very light', 'Light'],
      clarityCats: ['Flawless', 'VVS', 'VS', 'SI', 'Included'],
      cutGrades: ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'],
      every: 'Every KARAAT diamond', legend: 'Gold: light returns to the eye · Dashed: light lost',
      cutBtns: ['Too shallow', 'Excellent', 'Too deep'],
      cutCaps: [
        '<b>Too shallow.</b> Light passes straight through and escapes out of the bottom. The diamond looks glassy and lifeless.',
        '<b>Excellent.</b> Light enters through the top, reflects off the lower facets and returns through the top as brilliance and fire.',
        '<b>Too deep.</b> Light bounces once and leaks out through the sides. The center of the diamond looks dark.'
      ],
      caratLabel: 'Carat weight', ref: '1.00 ct',
      caratLine: function (mm, pct, w) {
        return 'Round brilliant, about ' + mm + ' mm across. ' + (pct === 0 ? 'The 1.00 ct reference size.' : Math.abs(pct) + '% ' + (pct > 0 ? 'wider' : 'narrower') + ' than a 1.00 ct, at ' + w + '× the weight.');
      }
    },
    sv: {
      dec: ',', karaatQuality: 'KARAAT-kvalitet', karaatRange: 'KARAAT-spann',
      q: { excellent: 'Utmärkt', high: 'Hög', good: 'Bra' },
      popular: 'Populärast', color: 'Färg', clarity: 'Klarhet', cut: 'Slipning',
      read: {
        excellent: 'D–F färg · VVS klarhet · slipning Utmärkt',
        high: 'D–F färg · VS klarhet · slipning Utmärkt · populärast',
        good: 'G–H färg · VS klarhet · slipning Utmärkt'
      },
      cards: {
        excellent: ['Helt färglös och nästan fri från inneslutningar, även i 10× förstoring.', 'Passar dig som inte vill kompromissa. Den vitaste och klaraste diamanten vi erbjuder.'],
        high: ['Helt färglös, med inneslutningar som bara syns med lupp.', 'Passar dig som vill ha en diamant som är färglös för ögat och ren under lupp, utan att ge avkall på något du skulle märka.'],
        good: ['Nästan färglös, med inneslutningar som bara syns med lupp.', 'Passar dig som vill gå så stort som möjligt. Här räknas varje millimeter.']
      },
      rows: {
        color: 'Hur vit den är. D är isvit; längre ner i alfabetet blir den gulare.',
        clarity: 'Hur ren den är inuti, sett i 10× förstoring.',
        cut: 'Hur väl den är slipad för att kasta tillbaka ljuset till ditt öga.'
      },
      colorCats: ['Färglös', 'Nästan färglös', 'Svag ton', 'Gulaktig', 'Gul'],
      clarityCats: ['Felfri', 'VVS', 'VS', 'SI', 'Inneslutningar'],
      cutGrades: ['Utmärkt', 'Mycket bra', 'Bra', 'Godtagbar', 'Dålig'],
      every: 'Varje KARAAT-diamant', legend: 'Guld: ljuset kommer tillbaka till ögat · Streckat: ljuset går förlorat',
      cutBtns: ['För grund', 'Utmärkt', 'För djup'],
      cutCaps: [
        '<b>För grund.</b> Ljuset passerar rakt igenom och försvinner ut genom botten. Diamanten ser glasartad och livlös ut.',
        '<b>Utmärkt.</b> Ljuset går in genom toppen, reflekteras mot de nedre fasetterna och kommer tillbaka ut genom toppen som briljans och eld.',
        '<b>För djup.</b> Ljuset studsar en gång och läcker ut genom sidorna. Diamantens mitt ser mörk ut.'
      ],
      caratLabel: 'Karatvikt', ref: '1,00 ct',
      caratLine: function (mm, pct, w) {
        return 'Rund briljant, ungefär ' + mm + ' mm i diameter. ' + (pct === 0 ? 'Referensstorleken 1,00 ct.' : Math.abs(pct) + ' % ' + (pct > 0 ? 'bredare' : 'smalare') + ' än en diamant på 1,00 ct, med ' + w + '× vikten.');
      }
    },
    fi: {
      dec: ',', karaatQuality: 'KARAAT-laatu', karaatRange: 'KARAAT-valikoima',
      q: { excellent: 'Erinomainen', high: 'Korkea', good: 'Hyvä' },
      popular: 'Suosituin', color: 'Väri', clarity: 'Puhtaus', cut: 'Hionta',
      read: {
        excellent: 'D–F väri · VVS puhtaus · Erinomainen hionta',
        high: 'D–F väri · VS puhtaus · Erinomainen hionta · suosituin',
        good: 'G–H väri · VS puhtaus · Erinomainen hionta'
      },
      cards: {
        excellent: ['Täysin väritön ja lähes sulkeumaton myös 10-kertaisella suurennoksella.', 'Sinulle, joka et halua tinkiä mistään. Valikoimamme valkoisin ja puhtain timantti.'],
        high: ['Täysin väritön, ja sulkeumat löytää vain luupilla.', 'Sinulle, joka haluat timantin, joka on silmälle väritön ja luupin alla puhdas, ilman kompromisseja, jotka huomaisit.'],
        good: ['Lähes väritön, ja sulkeumat löytää vain luupilla.', 'Sinulle, joka haluat mahdollisimman suuren timantin. Tässä jokainen millimetri ratkaisee.']
      },
      rows: {
        color: 'Kuinka valkoinen timantti on. D on jäänvalkoinen, ja mitä pidemmälle aakkosissa mennään, sitä kellertävämpi timantti on.',
        clarity: 'Kuinka puhdas timantti on sisältä. Pienet luonnolliset jäljet erottuvat vasta suurennoksella.',
        cut: 'Kuinka taitavasti timantti on hiottu. Hyvä hionta heijastaa valon takaisin silmään.'
      },
      colorCats: ['Väritön', 'Lähes väritön', 'Häivähdys', 'Kellertävä', 'Keltainen'],
      clarityCats: ['Virheetön', 'VVS', 'VS', 'SI', 'Sulkeumia'],
      cutGrades: ['Erinomainen', 'Erittäin hyvä', 'Hyvä', 'Kohtalainen', 'Heikko'],
      every: 'Jokainen KARAAT-timantti', legend: 'Kulta: valo heijastuu silmään · Katkoviiva: valo karkaa',
      cutBtns: ['Liian matala', 'Erinomainen', 'Liian syvä'],
      cutCaps: [
        '<b>Liian matala.</b> Valo kulkee suoraan läpi ja karkaa pohjasta. Timantti näyttää lasimaiselta ja elottomalta.',
        '<b>Erinomainen.</b> Valo tulee sisään yläpinnasta, heijastuu alemmista faseteista ja suuntautuu takaisin ylöspäin loistona ja tulena.',
        '<b>Liian syvä.</b> Valo heijastuu kerran ja vuotaa ulos sivuista. Timantin keskusta näyttää tummalta.'
      ],
      caratLabel: 'Karaattipaino', ref: '1,00 ct',
      caratLine: function (mm, pct, w) {
        return 'Pyöreä briljantti, halkaisija noin ' + mm + ' mm. ' + (pct === 0 ? 'Vertailukoko 1,00 ct.' : Math.abs(pct) + ' % ' + (pct > 0 ? 'leveämpi' : 'kapeampi') + ' kuin 1,00 ct:n timantti, ' + w + '-kertainen paino.');
      }
    }
  };

  function lang() {
    var l = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
    return STRINGS[l] ? l : 'en';
  }

  /* ------------------------------------------------------------------ helpers */
  function el(tag, attrs, parent) {
    var e = document.createElementNS(NS, tag);
    for (var k in attrs) e.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(e);
    return e;
  }
  function txt(g, x, y, s, cls) { var t = el('text', { x: x, y: y, 'text-anchor': 'middle', 'class': cls || '' }, g); t.textContent = s; return t; }
  function h(tag, cls, html) { var e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; }
  function fmt(n, d, S) { return n.toFixed(d).replace('.', S.dec); }
  function xs(n, m) { m = m || 40; var W = 940, out = []; for (var i = 0; i < n; i++) out.push(m + i * (W - 2 * m) / (n - 1)); return out; }
  function tint(i) { var t = i / 22; return 'hsl(46 ' + Math.round(15 + t * 75) + '% ' + Math.round(97 - t * 30) + '%)'; }

  function topGem(g, cx, cy, r, fill, stroke) {
    el('circle', { cx: cx, cy: cy, r: r, fill: fill, stroke: stroke, 'stroke-width': 1.5 }, g);
    function pts(n) { var a = []; for (var i = 0; i < 8; i++) { var ang = Math.PI / 8 + i * Math.PI / 4; a.push([cx + Math.cos(ang) * r * n, cy + Math.sin(ang) * r * n]); } return a; }
    var t = pts(0.52), o = pts(1);
    el('polygon', { points: t.map(function (p) { return p.join(','); }).join(' '), fill: 'none', stroke: stroke, 'stroke-width': 1, opacity: 0.8 }, g);
    for (var i = 0; i < 8; i++) {
      var a = t[i], b = t[(i + 1) % 8], m = Math.PI / 8 + i * Math.PI / 4 + Math.PI / 8;
      var s = [cx + Math.cos(m) * r, cy + Math.sin(m) * r];
      el('polyline', { points: a.join(',') + ' ' + s.join(',') + ' ' + b.join(','), fill: 'none', stroke: stroke, 'stroke-width': 0.8, opacity: 0.6 }, g);
      el('line', { x1: a[0], y1: a[1], x2: o[i][0], y2: o[i][1], stroke: stroke, 'stroke-width': 0.6, opacity: 0.4 }, g);
    }
    return g;
  }

  function highlight(g, x1, x2, label, dashed, hgt, pad) {
    pad = pad || 34;
    el('rect', { x: x1 - pad, y: 0, width: x2 - x1 + 2 * pad, height: hgt || 96, rx: 12, fill: dashed ? 'none' : 'var(--kdm-wash)', stroke: 'var(--kdm-gold)', 'stroke-width': dashed ? 1 : 1.2, 'stroke-dasharray': dashed ? '4 4' : 'none' }, g);
    if (label) txt(g, (x1 + x2) / 2, -6, label, 'kdm-hl');
  }
  function cats(g, x, list) {
    var step = x[1] - x[0];
    list.forEach(function (c) {
      el('line', { x1: x[c[1]] - step / 2 + 5, y1: 102, x2: x[c[2]] + step / 2 - 5, y2: 102, stroke: 'var(--kdm-line)' }, g);
      txt(g, (x[c[1]] + x[c[2]]) / 2, 129, c[0], 'kdm-cat');
    });
  }
  function svgBox(h_) { var s = el('svg', { viewBox: '0 -22 940 ' + h_, 'class': 'kdm-svg', role: 'img' }); var hl = el('g', {}, s), base = el('g', {}, s); return [s, hl, base]; }

  /* ------------------------------------------------------------------ scale rows */
  var COLOR_L = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N–R', 'S–Z'], COLOR_T = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 19];
  var CLAR_L = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'I1', 'I2', 'I3'], CLAR_N = [0, 0, 1, 2, 3, 4, 7, 10, 14, 19, 26];
  var QUAL = { excellent: { c: [0, 2], k: [2, 3] }, high: { c: [0, 2], k: [4, 5] }, good: { c: [3, 4], k: [4, 5] } };

  function colorRow(S) {
    var b = svgBox(162), x = xs(12);
    b[0].setAttribute('aria-label', S.color + ' D–Z');
    COLOR_L.forEach(function (l, i) { topGem(el('g', {}, b[2]), x[i], 44, 22, tint(COLOR_T[i]), 'var(--kdm-edge)'); txt(b[2], x[i], 86, l, 'kdm-g'); });
    cats(b[2], x, [[S.colorCats[0], 0, 2], [S.colorCats[1], 3, 6], [S.colorCats[2], 7, 9], [S.colorCats[3], 10, 10], [S.colorCats[4], 11, 11]]);
    return { svg: b[0], hl: b[1], x: x };
  }
  function clarityRow(S) {
    var b = svgBox(162), x = xs(11), seed = 11;
    function rnd() { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; }
    var pts = []; for (var p = 0; p < 26; p++) { var a = rnd() * Math.PI * 2, d = Math.sqrt(rnd()) * 0.72; pts.push([Math.cos(a) * d, Math.sin(a) * d, 0.55 + rnd() * 0.9, rnd()]); }
    b[0].setAttribute('aria-label', S.clarity + ' FL–I3');
    CLAR_L.forEach(function (l, i) {
      var g = topGem(el('g', {}, b[2]), x[i], 44, 22, 'var(--kdm-stone)', 'var(--kdm-edge)'), grow = 1 + Math.max(0, i - 5) * 0.32;
      pts.slice(0, CLAR_N[i]).forEach(function (q) {
        var X = x[i] + q[0] * 22, Y = 44 + q[1] * 22;
        if (q[3] > 0.65) el('line', { x1: X - q[2] * 2 * grow, y1: Y - q[2] * 0.6 * grow, x2: X + q[2] * 2 * grow, y2: Y + q[2] * 0.4 * grow, stroke: 'var(--kdm-ink)', 'stroke-width': 0.9, 'stroke-linecap': 'round', opacity: 0.6 }, g);
        else el('circle', { cx: X, cy: Y, r: q[2] * grow, fill: 'var(--kdm-ink)', opacity: 0.55 }, g);
      });
      txt(b[2], x[i], 86, l, 'kdm-g');
    });
    cats(b[2], x, [[S.clarityCats[0], 0, 1], [S.clarityCats[1], 2, 3], [S.clarityCats[2], 4, 5], [S.clarityCats[3], 6, 7], [S.clarityCats[4], 8, 10]]);
    return { svg: b[0], hl: b[1], x: x };
  }
  function cutRow(S) {
    var b = svgBox(144), x = xs(5, 90), D = [40, 46, 32, 52, 22], OK = [3, 2, 2, 1, 0];
    b[0].setAttribute('aria-label', S.cut);
    x.forEach(function (cx, i) {
      var g = el('g', {}, b[2]), top = 22, gy = 34, half = 50, th = 24, d = D[i];
      el('polygon', { points: (cx - th) + ',' + top + ' ' + (cx + th) + ',' + top + ' ' + (cx + half) + ',' + gy + ' ' + cx + ',' + (gy + d) + ' ' + (cx - half) + ',' + gy, fill: 'var(--kdm-stone)', stroke: 'var(--kdm-edge)', 'stroke-width': 1.2 }, g);
      el('line', { x1: cx - half, y1: gy, x2: cx + half, y2: gy, stroke: 'var(--kdm-edge)', 'stroke-width': 0.7 }, g);
      [-21, -12, -3].forEach(function (o, j) {
        var yf = gy + d * (half + o) / half;
        if (j < OK[i]) el('path', { d: 'M' + (cx + o) + ' 6 L' + (cx + o) + ' ' + yf + ' L' + (cx - o) + ' ' + yf + ' L' + (cx - o) + ' 4', fill: 'none', stroke: 'var(--kdm-gold)', 'stroke-width': 1.4, 'stroke-linejoin': 'round' }, g);
        else el('path', { d: 'M' + (cx + o) + ' 6 L' + (cx + o) + ' ' + yf + ' L' + (cx + o - 12) + ' ' + (yf + 18), fill: 'none', stroke: 'var(--kdm-leak)', 'stroke-width': 1.3, 'stroke-dasharray': '3 3' }, g);
      });
      txt(b[2], cx, 112, S.cutGrades[i], 'kdm-g');
    });
    highlight(b[1], x[0], x[0], S.every, false, 122, 70);
    txt(b[2], (x[1] + x[4]) / 2, -6, S.legend, 'kdm-cat');
    return { svg: b[0], hl: b[1], x: x };
  }

  /* ------------------------------------------------------------------ modules */
  function qualityToggle(S, onChange, initial) {
    var wrap = h('div', 'kdm-toggle');
    wrap.appendChild(h('span', 'kdm-toggle__label', S.karaatQuality));
    var seg = h('div', 'kdm-seg'); seg.setAttribute('role', 'group'); seg.setAttribute('aria-label', S.karaatQuality);
    var read = h('p', 'kdm-toggle__read'); read.setAttribute('aria-live', 'polite');
    ['excellent', 'high', 'good'].forEach(function (q) {
      var btn = h('button', '', S.q[q]); btn.type = 'button'; btn.dataset.q = q;
      btn.addEventListener('click', function () { set(q); });
      seg.appendChild(btn);
    });
    function set(q) {
      [].forEach.call(seg.children, function (b) { b.setAttribute('aria-pressed', b.dataset.q === q ? 'true' : 'false'); });
      read.innerHTML = '<b>' + S.q[q] + '</b> · ' + S.read[q];
      onChange(q);
    }
    wrap.appendChild(seg); wrap.appendChild(read);
    return { el: wrap, set: set, initial: initial || 'high' };
  }

  function applyQuality(row, range, S, q, rangeAll) {
    row.hl.innerHTML = '';
    if (q) {
      highlight(row.hl, row.x[rangeAll[0]], row.x[rangeAll[1]], '', true);
      highlight(row.hl, row.x[range[0]], row.x[range[1]], 'KARAAT ' + S.q[q], false);
    } else {
      highlight(row.hl, row.x[rangeAll[0]], row.x[rangeAll[1]], S.karaatRange, false);
    }
  }

  function rowBlock(title, desc, svg) {
    var r = h('div', 'kdm-row');
    var l = h('div', 'kdm-row__text'); l.appendChild(h('h4', 'kdm-row__title', title)); l.appendChild(h('p', 'kdm-row__desc', desc));
    r.appendChild(l); r.appendChild(svg);
    return r;
  }

  var MODULES = {
    'qualities': function (S) {
      var root = h('div', 'kdm kdm-qualities');
      var data = { excellent: ['D–F', 'VVS'], high: ['D–F', 'VS'], good: ['G–H', 'VS'] };
      ['excellent', 'high', 'good'].forEach(function (q) {
        var c = h('article', 'kdm-qcard' + (q === 'high' ? ' is-pick' : ''));
        if (q === 'high') c.appendChild(h('span', 'kdm-badge', S.popular));
        c.appendChild(h('h4', 'kdm-qcard__name', S.q[q]));
        c.appendChild(h('dl', 'kdm-qcard__spec', '<div><dt>' + S.color + '</dt><dd>' + data[q][0] + '</dd></div><div><dt>' + S.clarity + '</dt><dd>' + data[q][1] + '</dd></div><div><dt>' + S.cut + '</dt><dd>' + S.q.excellent + '</dd></div>'));
        c.appendChild(h('p', 'kdm-qcard__desc', S.cards[q][0]));
        c.appendChild(h('p', 'kdm-qcard__best', S.cards[q][1]));
        root.appendChild(c);
      });
      return root;
    },
    '4c-glance': function (S) {
      var root = h('div', 'kdm kdm-glance');
      var cR = colorRow(S), kR = clarityRow(S), uR = cutRow(S);
      var tog = qualityToggle(S, function (q) {
        applyQuality(cR, QUAL[q].c, S, q, [0, 4]);
        applyQuality(kR, QUAL[q].k, S, q, [2, 5]);
      });
      root.appendChild(tog.el);
      root.appendChild(rowBlock(S.color, S.rows.color, cR.svg));
      root.appendChild(rowBlock(S.clarity, S.rows.clarity, kR.svg));
      root.appendChild(rowBlock(S.cut, S.rows.cut, uR.svg));
      tog.set(tog.initial);
      return root;
    },
    'color': function (S) {
      var root = h('div', 'kdm kdm-single'), r = colorRow(S);
      applyQuality(r, null, S, null, [0, 4]);
      root.appendChild(r.svg); return root;
    },
    'clarity': function (S) {
      var root = h('div', 'kdm kdm-single'), r = clarityRow(S);
      applyQuality(r, null, S, null, [2, 5]);
      root.appendChild(r.svg); return root;
    },
    'cut': function (S) {
      var root = h('div', 'kdm kdm-cut');
      var s = el('svg', { viewBox: '0 0 400 300', 'class': 'kdm-cutsvg', role: 'img', 'aria-label': S.cut });
      var gem = el('polygon', { fill: 'var(--kdm-stone)', stroke: 'var(--kdm-edge)', 'stroke-width': 1.5 }, s);
      var fac = el('g', { stroke: 'var(--kdm-edge)', 'stroke-width': 0.8, fill: 'none', opacity: 0.7 }, s);
      var rays = el('g', { fill: 'none', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, s);
      var defs = el('defs', {}, s);
      [['kdm-ah', 'var(--kdm-gold)'], ['kdm-al', 'var(--kdm-leak)']].forEach(function (m) {
        var mk = el('marker', { id: m[0], viewBox: '0 0 10 10', refX: 6, refY: 5, markerWidth: 7, markerHeight: 7, orient: 'auto-start-reverse' }, defs);
        el('path', { d: 'M0 0L10 5L0 10z', fill: m[1] }, mk);
      });
      var CUTS = [
        { culet: 180, rays: [['M170 18 L170 165 L140 290', 0], ['M230 18 L230 165 L262 290', 0]] },
        { culet: 231, rays: [['M170 18 L170 205 L230 205 L230 18', 1], ['M150 18 L150 188 L250 188 L250 18', 1]] },
        { culet: 290, rays: [['M170 18 L170 251 L230 251 L392 196', 0]] }
      ];
      var cap = h('p', 'kdm-cut__cap'); cap.setAttribute('aria-live', 'polite');
      var seg = h('div', 'kdm-seg');
      function draw(k) {
        var c = CUTS[k];
        gem.setAttribute('points', '140,70 260,70 340,110 200,' + c.culet + ' 60,110');
        fac.innerHTML = ''; rays.innerHTML = '';
        [[60, 110, 340, 110], [140, 70, 110, 110], [260, 70, 290, 110], [140, 70, 170, 110], [260, 70, 230, 110], [110, 110, 200, c.culet], [290, 110, 200, c.culet]].forEach(function (l) { el('line', { x1: l[0], y1: l[1], x2: l[2], y2: l[3] }, fac); });
        c.rays.forEach(function (r) { el('path', { d: r[0], stroke: r[1] ? 'var(--kdm-gold)' : 'var(--kdm-leak)', 'stroke-dasharray': r[1] ? 'none' : '6 5', 'marker-end': r[1] ? 'url(#kdm-ah)' : 'url(#kdm-al)' }, rays); });
        cap.innerHTML = S.cutCaps[k];
        [].forEach.call(seg.children, function (b, j) { b.setAttribute('aria-pressed', j === k ? 'true' : 'false'); });
      }
      S.cutBtns.forEach(function (t, k) { var b = h('button', '', t); b.type = 'button'; b.addEventListener('click', function () { draw(k); }); seg.appendChild(b); });
      root.appendChild(s); root.appendChild(seg); root.appendChild(cap);
      draw(1);
      return root;
    },
    'carat': function (S) {
      var CT = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3], MM = [4.1, 5.2, 5.8, 6.5, 6.9, 7.4, 8.2, 8.8, 9.4], SC = 11;
      var root = h('div', 'kdm kdm-carat');
      var s = el('svg', { viewBox: '0 0 460 230', 'class': 'kdm-caratsvg', 'aria-hidden': 'true' });
      var g = el('g', {}, s);
      var big = h('div', 'kdm-carat__val'), line = h('p', 'kdm-carat__line');
      var range = document.createElement('input');
      range.type = 'range'; range.min = 0; range.max = 8; range.step = 1; range.value = 3; range.setAttribute('aria-label', S.caratLabel);
      var ticks = h('div', 'kdm-carat__ticks', CT.map(function (c) { return '<span>' + fmt(c, 2, S) + '</span>'; }).join(''));
      function draw(k) {
        g.innerHTML = '';
        el('circle', { cx: 110, cy: 105, r: MM[3] * SC / 2, fill: 'none', stroke: 'var(--kdm-edge)', 'stroke-dasharray': '3 4' }, g);
        txt(g, 110, 105 + MM[3] * SC / 2 + 24, S.ref, 'kdm-cat');
        topGem(el('g', {}, g), 310, 105, MM[k] * SC / 2, 'var(--kdm-stone)', 'var(--kdm-gold-soft)');
        var r = MM[k] * SC / 2, y = 105 + r + 16;
        el('line', { x1: 310 - r, y1: y, x2: 310 + r, y2: y, stroke: 'var(--kdm-gold)' }, g);
        el('line', { x1: 310 - r, y1: y - 5, x2: 310 - r, y2: y + 5, stroke: 'var(--kdm-gold)' }, g);
        el('line', { x1: 310 + r, y1: y - 5, x2: 310 + r, y2: y + 5, stroke: 'var(--kdm-gold)' }, g);
        txt(g, 310, y + 20, '≈ ' + fmt(MM[k], 1, S) + ' mm', 'kdm-hl');
        big.innerHTML = fmt(CT[k], 2, S) + '<small> ct</small>';
        var pct = k === 3 ? 0 : Math.round((MM[k] / MM[3] - 1) * 100);
        line.textContent = S.caratLine(fmt(MM[k], 1, S), pct, fmt(CT[k], 2, S));
      }
      range.addEventListener('input', function () { draw(+range.value); });
      root.appendChild(s); root.appendChild(big); root.appendChild(line); root.appendChild(range); root.appendChild(ticks);
      draw(3);
      return root;
    }
  };

  /* ------------------------------------------------------------------ mount */
  function mount(scope) {
    var S = STRINGS[lang()];
    var nodes = (scope || document).querySelectorAll('p, div[data-karaat-module]');
    [].forEach.call(nodes, function (n) {
      var name = n.getAttribute('data-karaat-module');
      if (!name) {
        var m = (n.textContent || '').trim().match(/^\[\[karaat-([a-z0-9-]+)\]\]$/);
        if (!m) return;
        name = m[1];
      }
      if (!MODULES[name] || n.dataset.kdmMounted) return;
      var out = MODULES[name](S);
      out.dataset.kdmMounted = '1';
      n.replaceWith(out);
    });
  }

  window.KaraatDiamondModules = { mount: mount, strings: STRINGS };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { mount(); });
  else mount();
})();
